import { useEffect, useRef, useState } from 'react';
import { apiRequest } from '../../utils/api';
import {
  hasSubscriptionAddress,
  toggleBuildingSubscription,
} from '../../utils/buildingSubscription';
import type {
  Address,
  BuildingWithConvertedAddresses,
} from '../../utils/types';

type Options = {
  building: BuildingWithConvertedAddresses;
  address: Address | null;
  addressLoading: boolean;
  isLoggedIn: boolean;
  onLoginRequired: () => void;
  onError: () => void;
  onChanged: (result: {
    is_subscribed: boolean;
    subscribersCount?: number;
  }) => void;
};

export function useBuildingSubscription(options: Options) {
  const { building, address, addressLoading, isLoggedIn } = options;
  const callbacks = useRef(options);
  useEffect(() => {
    callbacks.current = options;
  }, [options]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatusReady, setIsStatusReady] = useState(false);
  const [statusRetry, setStatusRetry] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const generation = useRef(0);
  const pending = useRef(false);

  useEffect(() => {
    const requestGeneration = ++generation.current;
    pending.current = false;
    setIsSaving(false);
    setIsSubscribed(false);
    setIsStatusReady(false);
    setIsLoading(isLoggedIn);
    if (!isLoggedIn) return;
    apiRequest(`/is-subscribed?building-id=${encodeURIComponent(building.id)}`)
      .then((data: { is_subscribed: boolean }) => {
        if (requestGeneration !== generation.current) return;
        if (typeof data?.is_subscribed !== 'boolean')
          throw new Error('Invalid subscription status');
        setIsSubscribed(data.is_subscribed);
        setIsStatusReady(true);
      })
      .catch(() => {
        if (requestGeneration === generation.current)
          callbacks.current.onError();
      })
      .finally(() => {
        if (requestGeneration === generation.current) setIsLoading(false);
      });
    return () => {
      generation.current = requestGeneration + 1;
    };
  }, [isLoggedIn, building.id, statusRetry]);

  // An existing follow can be removed even if its address cannot resolve.
  const addressPending =
    !isSubscribed && (addressLoading || !hasSubscriptionAddress(address));
  const disabled =
    isLoggedIn && (isLoading || isSaving || (isStatusReady && addressPending));

  const toggle = async () => {
    if (!isLoggedIn) {
      callbacks.current.onLoginRequired();
      return;
    }
    if (disabled || pending.current) return;
    if (!isStatusReady) {
      // A failed read must not be interpreted as "not followed". Retry that
      // read first, so a later click expresses the user's actual intent.
      setIsLoading(true);
      setStatusRetry((attempt) => attempt + 1);
      return;
    }
    pending.current = true;
    setIsSaving(true);
    const requestGeneration = generation.current;
    try {
      const result = await toggleBuildingSubscription(building, address);
      if (requestGeneration !== generation.current) return;
      setIsSubscribed(result.is_subscribed);
      callbacks.current.onChanged(result);
    } catch {
      if (requestGeneration === generation.current) callbacks.current.onError();
    } finally {
      if (requestGeneration === generation.current) {
        pending.current = false;
        setIsSaving(false);
      }
    }
  };

  return {
    isSubscribed,
    setIsSubscribed,
    isSaving,
    addressPending,
    disabled,
    toggle,
  };
}
